import { CommonModule } from '@angular/common'
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractControl, FormControl, Validators } from '@angular/forms'
import type { Icon } from '@ftr/foundation'
import {
  AnchorComponent,
  ButtonComponent,
  FocusableNoopAnchorDirective,
  IconComponent,
  OnClickOutsideDirective,
  StopPropagationDirective,
  TooltipModule,
} from '@ftr/foundation'
import { distinctUntilChanged, filter, Observable, take } from 'rxjs'
import { ValidationErrorHintDirective } from '../../directives/validation-error-hint'
import { SelectItem } from './select-item'

let nextId = 0
/**
 * A select menu component similar to the ftr-select-search component but without the input
 */
@Component({
  selector: 'ftr-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.css'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    AnchorComponent,
    ButtonComponent,
    CommonModule,
    FocusableNoopAnchorDirective,
    IconComponent,
    OnClickOutsideDirective,
    StopPropagationDirective,
    TooltipModule,
    ValidationErrorHintDirective,
  ],
})
export class SelectComponent<TValue> implements OnInit {
  /**
   * The control that contains this components value
   */
  @Input() control: AbstractControl
  /**
   * The list of things the user needs to select one of
   */
  @Input() items$: Observable<SelectItem<TValue>[]>
  /**
   * The default value of the element
   */
  @Input() defaultValue?: TValue

  @Input() equals: (a: TValue | undefined | null, b: TValue | undefined | null) => boolean = (a, b) => a === b
  /**
   * A label for this control
   */
  @Input() label: string
  /**
   * A label for the singular version
   */
  @Input() singular: string
  /**
   * Whether the control is enabled and can be clicked
   */
  @Input() enabled = true
  /**
   * Puts the component in a highlighted state with a larger border when true
   */
  @Input() highlightError?: Observable<boolean>
  /**
   * Is the input required or optional (default optional)
   * Use this instead of setting Validators.required on the external control
   * @type {boolean}
   */
  @Input() required = false

  /**
   * When set, shows a leading icon on the selector component.
   */
  @Input() selectorIcon?: Icon

  /**
   * If the selected value needs to look different to what is in the drop down,
   * create a formatter function that receives the value and returns a new value
   */
  @Input() selectedItemFormatter?: Function
  /**
   * Hide the label that is shown above the input.
   * @type {boolean}
   */
  @Input() hideLabel = false
  /**
   * This is a temporary setting to enable select in the search-bar component
   * To be removed as a part of FTRX-5864
   * @type {boolean}
   */
  @Input() searchType = false
  /**
   * Whether submission of the form this component is part of has been attempted. When submission is attempted, the
   * underlying validation control (i.e.: ValidationHint) may choose to display validation states for fields which have
   * not yet been touched.
   */
  @Input() submitAttempted = false
  /**
   * When true (default), `select--has-value` styling is applied even for null/undefined so existing screens keep the
   * same layout. Set to false when null/undefined should use the empty label/value presentation like it is in ftr-date-picker (e.g. placeholder option).
   */
  @Input() alwaysShowValue = true
  /**
   * When true, shows a clear button when a value is selected and omits null/undefined placeholder options from the
   * dropdown. Use for optional selects where clearing replaces a "please select" entry.
   */
  @Input() showClearButton = false
  /**
   * Subscribe your component to this event to get notified when the array of items on this select changes
   */
  @Output() onItemsChange = new EventEmitter<SelectItem<TValue>[] | undefined>()
  /**
   * Subscribe your component to this event to get notified when the selection changes
   */
  @Output() onSelectionChange = new EventEmitter<TValue>()
  /**
   * Emits when the dropdown opens or closes so parents can react.
   */
  @Output() isOpenChange = new EventEmitter<boolean>()

  @Input() id = `ftr-select-${nextId++}`
  /**
   * Is the menu opened
   */
  selectedLabel: string
  isOpen = false
  activeDescendantId: string | null = null

  /**
   * The internal control that drives this component
   */
  internalControl: FormControl<TValue | null>

  #items: SelectItem<TValue>[] = []
  #destroyRef = inject(DestroyRef)

  @ViewChild('selectValue') selectValueButton?: ElementRef<HTMLElement>

  ngOnInit(): void {
    const initialValue = this.control.value
    const validators = []
    if (this.required) {
      validators.push(Validators.required)
    }

    // Update the external control validators
    this.control.setValidators(validators)
    this.internalControl = new FormControl(initialValue, validators)

    this.items$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(items => {
      this.#items = this.getDisplayItems(items)
      this.setDefaultValue(items)
      this.onItemsChange.emit(items)
      this.activeDescendantId = this.getActiveDescendant(this.#items)
    })

    this.control.valueChanges
      .pipe(
        filter(value => this.showClearButton || !!value),
        takeUntilDestroyed(this.#destroyRef),
        distinctUntilChanged((a, b) => this.equals(a, b)),
      )
      .subscribe(change => {
        if (this.equals(this.internalControl.value, change)) {
          return
        }
        this.internalControl.setValue(change)
        this.setSelectedLabel()
        this.activeDescendantId = this.getActiveDescendant(this.#items)
      })

    this.#destroyRef.onDestroy(() => this.close())
  }

  /**
   * Set the value to the control so it can be accessed by the consumer
   */
  setValue(value: TValue): void {
    this.internalControl.setValue(value)
    this.setSelectedLabel()
    this.onSelectionChange.emit(value)
    this.updateExternalControl(value)
    this.close()
    this.activeDescendantId = this.getActiveDescendant(this.#items)
  }

  clearValue(event?: Event): void {
    event?.stopPropagation()
    event?.preventDefault()
    const clearedValue = null as TValue | null
    this.internalControl.setValue(clearedValue)
    this.selectedLabel = ''
    this.updateExternalControl(clearedValue)
    this.onSelectionChange.emit(clearedValue as TValue)
    this.activeDescendantId = null
  }

  hasSelectedValue(): boolean {
    const value = this.internalControl?.value
    return value !== null && value !== undefined
  }

  get openedMenuLabel(): string {
    if (this.showClearButton && this.hasSelectedValue() && this.selectedLabel) {
      return this.selectedLabel
    }
    return this.selectPlaceholderLabel
  }

  get selectPlaceholderLabel(): string {
    return this.singular ? `Select ${this.singular}` : 'Select'
  }

  getActiveDescendant(items: SelectItem<TValue>[]): string | null {
    const index = items.findIndex(i => this.equals(i.value, this.control.value))
    return index > -1 ? `${this.id}-item-${index}` : null
  }

  /**
   * Close the drop down and mark the element touched
   */
  close(focusInput = false): void {
    if (!this.isOpen) {
      return
    }
    this.isOpen = false
    this.isOpenChange.emit(false)
    this.internalControl.markAsTouched({ onlySelf: true })

    if (focusInput) {
      this.focusSelectInput()
    }
  }

  /**
   * Open the drop down
   */
  open(): void {
    if (this.isOpen) {
      return
    }
    this.isOpen = true
    this.isOpenChange.emit(true)
    this.activeDescendantId = this.getActiveDescendant(this.#items)
  }

  @HostListener('keydown.Tab', ['$event'])
  onTabKey(event: Event): void {
    if (!this.isOpen) {
      return
    }

    event.preventDefault()
    this.selectNextOption(event)
  }

  @HostListener('keydown.Shift.Tab', ['$event'])
  onShiftTabKey(event: Event): void {
    if (!this.isOpen) {
      return
    }

    event.preventDefault()
    this.selectPreviousOption(event)
  }

  @HostListener('keydown.Escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (!this.isOpen) {
      return
    }

    event.preventDefault()
    this.close(true)
  }

  @HostListener('keydown.ArrowDown', ['$event'])
  selectNextOption(event: Event): void {
    if (!this.isOpen) {
      return
    }

    event.preventDefault()
    let nextOption: Element | undefined
    const currentOption = this.getCurrentOption()
    if (currentOption) {
      currentOption.classList.remove('select__item--hover')
      nextOption = currentOption?.nextElementSibling ? currentOption.nextElementSibling : this.getFirstOption()
    } else {
      nextOption = this.getFirstOption()
    }
    if (nextOption) {
      nextOption.classList.add('select__item--hover')
      nextOption.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'start' })
      this.activeDescendantId = nextOption.id
    }
  }
  @HostListener('keydown.ArrowUp', ['$event'])
  selectPreviousOption(event: Event): void {
    if (!this.isOpen) {
      return
    }

    event.preventDefault()
    let previousOption: Element | undefined
    const currentOption = this.getCurrentOption()
    if (currentOption) {
      previousOption = currentOption?.previousElementSibling
        ? currentOption.previousElementSibling
        : this.getLastOption()
      currentOption.classList.remove('select__item--hover')
    } else {
      previousOption = this.getLastOption()
    }
    if (previousOption) {
      previousOption.classList.add('select__item--hover')
      previousOption.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'start' })
      this.activeDescendantId = previousOption.id
    }
  }

  @HostListener('keydown.Enter', ['$event'])
  @HostListener('keydown.Space', ['$event'])
  openDropdownAndSelectCurrentOption(event: Event): void {
    event.preventDefault()
    if (!this.isOpen) {
      this.open()
    } else {
      const currentOption = this.getCurrentOption()
      if (currentOption) {
        const optionValue = this.#items.find(o => o.key.trim() === currentOption.innerHTML.trim())?.value
        if (optionValue) {
          this.setValue(optionValue)
        }
      }
    }
  }

  /**
   * Set the default selected value. If that value doesn't exist in the current list,
   * set the value to the first entry
   */
  private setDefaultValue(items: SelectItem<TValue>[]): void {
    const displayItems = this.getDisplayItems(items)
    const hasValidSelection = displayItems.some(item => this.equals(item.value, this.internalControl.value))

    if (!hasValidSelection) {
      const hasValidDefault =
        this.defaultValue !== undefined &&
        this.defaultValue !== null &&
        displayItems.some(item => this.equals(item.value, this.defaultValue))

      if (hasValidDefault) {
        this.internalControl.setValue(this.defaultValue!)
      } else if (this.showClearButton) {
        this.internalControl.setValue(null)
      } else if (items.length) {
        this.internalControl.setValue(items[0].value)
      } else {
        this.internalControl.setValue(null)
      }
    }

    this.updateExternalControl(this.internalControl.value)
    this.setSelectedLabel()
  }

  getDisplayItems(items: SelectItem<TValue>[]): SelectItem<TValue>[] {
    if (!this.showClearButton) {
      return items
    }

    return items.filter(item => item.value !== null && item.value !== undefined)
  }

  /**
   * Set the label used to display what is selected.
   * This finds the key from the list of items, and optionally passes it through a formatter
   * if you want an option's value to display differently in the label than in the dropdown.
   */
  private setSelectedLabel(): void {
    if (this.selectedItemFormatter) {
      const label = this.selectedItemFormatter(this.internalControl.value)
      if (label !== null) {
        this.selectedLabel = label
        return
      }
    }

    this.items$.pipe(take(1)).subscribe(items => {
      const label = items.find(item => this.equals(item.value, this.internalControl.value))
      this.selectedLabel = label ? label.key : ''
    })
  }

  /**
   * Update the external control and its validity, ONLY if its different.
   * E.G. do not update the control if its already that value, otherwise we may get into a loop.
   * @param value
   */
  private updateExternalControl(value: TValue | null): void {
    if (!this.equals(this.control.value, value)) {
      this.control.setValue(value)
      this.control.markAsTouched({ onlySelf: true })
      this.control.updateValueAndValidity()
    }
  }
  // When two select inputs are used on the same page
  // one must set the Id input to distinguish between them
  private getCurrentOption(): Element | undefined {
    return (
      document.querySelector<Element>(`div[id^='${this.id}'] div.select__item--hover`) ??
      document.querySelector<Element>(`div[id^='${this.id}'] div.select__item--selected`) ??
      undefined
    )
  }

  private getFirstOption(): Element | undefined {
    const options = Array.from(document.querySelectorAll<Element>(`div[id^='${this.id}'] div.select__item`))
    return options.at(0)
  }

  private getLastOption(): Element | undefined {
    const options = Array.from(document.querySelectorAll<Element>(`div[id^='${this.id}'] div.select__item`))
    return options.at(-1)
  }

  private focusSelectInput(): void {
    this.selectValueButton?.nativeElement.focus()
  }
}
